<template>
  <div class="auth-page">
    <!-- Left panel -->
    <div class="auth-left">
      <div class="auth-left-content">
        <div class="brand-logo">
          <img src="@/assets/brand/logo-main-light.svg" alt="Sequire" class="brand-logo-img" />
        </div>
        <h1 class="auth-tagline">The Future of<br />Stock Surveillance</h1>
        <p class="auth-description">
          Sequire unlocks stock buyers' behaviors and trends for issuers of publicly traded companies.
        </p>
      </div>
      <p class="powered-by">Powered by <strong>SRAX.</strong></p>
    </div>

    <!-- Right panel -->
    <div class="auth-right">
      <div class="auth-form-wrapper">
        <div class="form-logo">
          <img src="@/assets/brand/logo-main-light.svg" alt="Sequire" class="form-logo-img" />
        </div>

        <!-- Stepper -->
        <div class="stepper">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="stepper-item"
            :class="{ active: currentStep === i, done: currentStep > i }"
          >
            <div class="stepper-circle">
              <span v-if="currentStep <= i">{{ i + 1 }}</span>
              <i v-else class="el-icon-check" />
            </div>
            <span class="stepper-label">{{ step }}</span>
            <div v-if="i < steps.length - 1" class="stepper-line" />
          </div>
        </div>

        <!-- Step 1: Personal Information -->
        <div v-show="currentStep === 0">
          <h2 class="form-title">Personal information</h2>
          <div class="form-title-bar" />

          <el-form
            ref="personalForm"
            :model="form"
            :rules="personalRules"
            label-position="top"
            class="signup-form"
          >
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="First name" prop="first_name">
                  <el-input v-model="form.first_name" placeholder="Jane" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Last name" prop="last_name">
                  <el-input v-model="form.last_name" placeholder="Smith" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item label="Email" prop="email">
              <el-input v-model="form.email" type="email" placeholder="jane@company.com" />
              <p class="field-hint">This will be used for your login.</p>
            </el-form-item>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="Password" prop="password">
                  <el-input v-model="form.password" type="password" placeholder="Min. 8 characters" show-password @input="onPasswordInput">
                    <el-button slot="append" @click="generatePassword" title="Generate password">
                      <i class="el-icon-refresh" />
                    </el-button>
                  </el-input>
                  <div v-if="form.password" class="pwd-rules">
                    <span :class="['pwd-rule', pwdRules.uppercase ? 'rule-ok' : 'rule-fail']">
                      <i :class="pwdRules.uppercase ? 'el-icon-check' : 'el-icon-close'" /> Uppercase
                    </span>
                    <span :class="['pwd-rule', pwdRules.lowercase ? 'rule-ok' : 'rule-fail']">
                      <i :class="pwdRules.lowercase ? 'el-icon-check' : 'el-icon-close'" /> Lowercase
                    </span>
                    <span :class="['pwd-rule', pwdRules.numeric ? 'rule-ok' : 'rule-fail']">
                      <i :class="pwdRules.numeric ? 'el-icon-check' : 'el-icon-close'" /> Number
                    </span>
                    <span :class="['pwd-rule', pwdRules.special ? 'rule-ok' : 'rule-fail']">
                      <i :class="pwdRules.special ? 'el-icon-check' : 'el-icon-close'" /> Special char
                    </span>
                  </div>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Confirm password" prop="confirm_password">
                  <el-input v-model="form.confirm_password" type="password" placeholder="Repeat password" show-password />
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="Job title" prop="job_title">
                  <el-input v-model="form.job_title" placeholder="CFO, Analyst..." />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Phone" prop="phone">
                  <el-input v-model="form.phone" placeholder="+1 (555) 000-0000" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-button type="primary" size="large" class="btn-submit" @click="nextStep(0)">
              Continue
            </el-button>
          </el-form>
        </div>

        <!-- Step 2: Company -->
        <div v-show="currentStep === 1">
          <h2 class="form-title">Create company</h2>
          <div class="form-title-bar" />

          <el-form
            ref="companyForm"
            :model="form"
            :rules="companyRules"
            label-position="top"
            class="signup-form"
          >
            <!-- Lookup mode toggle -->
            <div class="lookup-toggle">
              <span class="lookup-toggle-label">Look up company by</span>
              <el-radio-group v-model="lookupMode" size="small" @change="onLookupModeChange">
                <el-radio-button label="ticker">Ticker Symbol</el-radio-button>
                <el-radio-button label="cik">CIK (SEC)</el-radio-button>
              </el-radio-group>
            </div>

            <!-- Ticker lookup input -->
            <template v-if="lookupMode === 'ticker'">
              <el-form-item label="Ticker symbol" prop="company_ticker">
                <el-input
                  v-model="form.company_ticker"
                  placeholder="AAPL"
                  :suffix-icon="tickerLooking ? 'el-icon-loading' : tickerStatus === 'valid' ? 'el-icon-circle-check' : tickerStatus === 'invalid' ? 'el-icon-circle-close' : ''"
                  @blur="lookupTickerField"
                />
                <div v-if="tickerStatus === 'invalid'" class="cik-feedback invalid">
                  <i class="el-icon-warning" /> Ticker not found in SEC EDGAR
                </div>
              </el-form-item>

              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="Company name" prop="company_name">
                    <el-input v-model="form.company_name" placeholder="—" disabled />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="CIK (SEC)" prop="company_cik">
                    <el-input v-model="form.company_cik" placeholder="—" />
                  </el-form-item>
                </el-col>
              </el-row>
            </template>

            <!-- CIK lookup input -->
            <template v-if="lookupMode === 'cik'">
              <el-form-item label="CIK (SEC)" prop="company_cik">
                <el-input
                  v-model="form.company_cik"
                  placeholder="0001234567"
                  :suffix-icon="cikValidating ? 'el-icon-loading' : cikStatus === 'valid' ? 'el-icon-circle-check' : cikStatus === 'invalid' ? 'el-icon-circle-close' : ''"
                  @blur="validateCikField"
                />
                <div v-if="cikStatus === 'invalid'" class="cik-feedback invalid">
                  <i class="el-icon-warning" /> CIK not found in SEC EDGAR
                </div>
              </el-form-item>

              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="Company name" prop="company_name">
                    <el-input v-model="form.company_name" placeholder="—" disabled />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="Ticker symbol" prop="company_ticker">
                    <el-input v-model="form.company_ticker" placeholder="—" />
                  </el-form-item>
                </el-col>
              </el-row>
            </template>

            <div class="btn-row">
              <el-button size="large" class="btn-back" @click="currentStep = 0">Back</el-button>
              <el-button type="primary" size="large" class="btn-submit" :loading="cikValidating || tickerLooking" @click="nextStep(1)">Continue</el-button>
            </div>
          </el-form>
        </div>

        <!-- Step 3: Review -->
        <div v-show="currentStep === 2">
          <h2 class="form-title">Review order</h2>
          <div class="form-title-bar" />

          <div class="review-section">
            <p class="review-section-title">Personal Information</p>
            <el-row :gutter="16">
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">First name</span>
                  <span class="review-value">{{ form.first_name }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">Last name</span>
                  <span class="review-value">{{ form.last_name }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">Email</span>
                  <span class="review-value">{{ form.email }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">Job title</span>
                  <span class="review-value">{{ form.job_title || "—" }}</span>
                </div>
              </el-col>
            </el-row>
          </div>

          <div class="review-section">
            <p class="review-section-title">Company</p>
            <el-row :gutter="16">
              <el-col :span="24">
                <div class="review-field">
                  <span class="review-label">Company name</span>
                  <span class="review-value">{{ form.company_name || "—" }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">Ticker</span>
                  <span class="review-value">{{ form.company_ticker || "—" }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="review-field">
                  <span class="review-label">CIK</span>
                  <span class="review-value">{{ form.company_cik || "—" }}</span>
                </div>
              </el-col>
            </el-row>
          </div>

          <div class="email-notice">
            <i class="el-icon-message" />
            A confirmation email will be sent to {{ form.email }} after registration.
          </div>

          <el-alert
            v-if="submitError"
            :title="submitError"
            type="error"
            show-icon
            class="submit-alert"
            :closable="true"
            @close="submitError = null"
          />

          <div class="btn-row">
            <el-button size="large" class="btn-back" :disabled="submitting" @click="currentStep = 1">Back</el-button>
            <el-button type="primary" size="large" class="btn-submit" :loading="submitting" @click="handleSubmit">
              {{ submitting ? "Creating your account..." : "Create Account" }}
            </el-button>
          </div>
        </div>

        <p class="alt-link">
          Already have an account?
          <router-link to="/login">Login</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { signup, validateCik, lookupByTicker } from "../api/auth";

export default {
  name: "SignupView",

  data() {
    const validateEmail = (rule, value, callback) => {
      if (!value) return callback(new Error("Email is required"));
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return callback(new Error("Please enter a valid email address"));
      callback();
    };

    const validatePhone = (rule, value, callback) => {
      if (!value) return callback();
      const phoneRegex = /^[+]?[\d\s\-().]{7,20}$/;
      if (!phoneRegex.test(value)) return callback(new Error("Please enter a valid phone number"));
      callback();
    };

    const validatePassword = (rule, value, callback) => {
      if (!value) return callback(new Error("Password is required"));
      if (value.length < 8) return callback(new Error("Password must be at least 8 characters"));
      if (!/[A-Z]/.test(value)) return callback(new Error("Password must contain an uppercase letter"));
      if (!/[a-z]/.test(value)) return callback(new Error("Password must contain a lowercase letter"));
      if (!/[0-9]/.test(value)) return callback(new Error("Password must contain a number"));
      if (!/[^A-Za-z0-9]/.test(value)) return callback(new Error("Password must contain a special character"));
      callback();
    };

    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) return callback(new Error("Please confirm your password"));
      if (value !== this.form.password) return callback(new Error("Passwords do not match"));
      callback();
    };

    return {
      currentStep: 0,
      submitting: false,
      submitError: null,
      steps: ["Personal information", "Create company", "Review order"],
      lookupMode: "ticker",   // 'ticker' | 'cik'
      cikValidating: false,
      cikStatus: null,        // null | 'valid' | 'invalid'
      cikCompanyName: "",
      tickerLooking: false,
      tickerStatus: null,     // null | 'valid' | 'invalid'
      tickerCompanyName: "",

      form: {
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        confirm_password: "",
        job_title: "",
        phone: "",
        company_name: "",
        company_ticker: "",
        company_cik: "",
      },

      personalRules: {
        first_name:       [{ required: true, message: "First name is required", trigger: "blur" }],
        last_name:        [{ required: true, message: "Last name is required",  trigger: "blur" }],
        email:            [{ required: true, validator: validateEmail,           trigger: "blur" }],
        phone:            [{ validator: validatePhone,                           trigger: "blur" }],
        password:         [{ required: true, validator: validatePassword,        trigger: ["blur", "change"] }],
        confirm_password: [{ required: true, validator: validateConfirmPassword, trigger: ["blur", "change"] }],
      },

    };
  },

  computed: {
    pwdRules() {
      const v = this.form.password;
      return {
        uppercase: /[A-Z]/.test(v),
        lowercase: /[a-z]/.test(v),
        numeric:   /[0-9]/.test(v),
        special:   /[^A-Za-z0-9]/.test(v),
      };
    },
    companyRules() {
      return {
        company_name:   [{ required: true, message: "Company name is required",   trigger: "blur" }],
        company_ticker: [{ required: true, message: "Ticker symbol is required",  trigger: "blur" }],
        company_cik:    [{ required: true, message: "CIK is required",            trigger: "blur" }],
      };
    },
  },

  methods: {
    onPasswordInput() {
      if (this.form.confirm_password) {
        this.$refs.personalForm.validateField("confirm_password");
      }
    },

    generatePassword() {
      const upper   = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const lower   = "abcdefghijklmnopqrstuvwxyz";
      const digits  = "0123456789";
      const special = "!@#$%^&*()-_=+[]{}|;:,.<>?";
      const all = upper + lower + digits + special;
      const len = 12;
      const buf = new Uint32Array(len + 4);
      window.crypto.getRandomValues(buf);
      const chars = [
        upper[buf[0] % upper.length],
        lower[buf[1] % lower.length],
        digits[buf[2] % digits.length],
        special[buf[3] % special.length],
        ...Array.from({ length: len - 4 }, (_, i) => all[buf[4 + i] % all.length]),
      ];
      const shuffle = new Uint32Array(len);
      window.crypto.getRandomValues(shuffle);
      for (let i = len - 1; i > 0; i--) {
        const j = shuffle[i] % (i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
      }
      this.form.password = chars.join("");
      this.form.confirm_password = chars.join("");
    },

    padCik(cik) {
      return cik ? String(cik).padStart(10, "0") : "";
    },

    onLookupModeChange() {
      this.cikStatus = null;
      this.cikCompanyName = "";
      this.tickerStatus = null;
      this.tickerCompanyName = "";
      this.form.company_name = "";
      this.form.company_cik = "";
      this.form.company_ticker = "";
      this.$nextTick(() => this.$refs.companyForm && this.$refs.companyForm.clearValidate());
    },

    async lookupTickerField() {
      const ticker = this.form.company_ticker.trim();
      if (!ticker) { this.tickerStatus = null; return; }
      this.tickerLooking = true;
      this.tickerStatus = null;
      try {
        const res = await lookupByTicker(ticker);
        this.tickerStatus = "valid";
        this.tickerCompanyName = res.data.name || "";
        this.form.company_name = res.data.name || "";
        this.form.company_cik = this.padCik(res.data.cik);
        this.form.company_ticker = res.data.ticker || ticker.toUpperCase();
      } catch {
        this.tickerStatus = "invalid";
        this.tickerCompanyName = "";
        this.form.company_name = "";
        this.form.company_cik = "";
      } finally {
        this.tickerLooking = false;
      }
    },

    async validateCikField() {
      const cik = this.padCik(this.form.company_cik.trim());
      if (!cik) { this.cikStatus = null; return; }
      this.form.company_cik = cik;
      this.cikValidating = true;
      this.cikStatus = null;
      try {
        const res = await validateCik(cik);
        this.cikStatus = "valid";
        this.cikCompanyName = res.data.name || "";
        if (!this.form.company_name) this.form.company_name = res.data.name || "";
        if (!this.form.company_ticker && res.data.ticker) this.form.company_ticker = res.data.ticker;
      } catch {
        this.cikStatus = "invalid";
        this.cikCompanyName = "";
        this.form.company_name = "";
        this.form.company_ticker = "";
      } finally {
        this.cikValidating = false;
      }
    },

    async nextStep(step) {
      if (step === 0) {
        this.$refs.personalForm.validate((valid) => { if (valid) this.currentStep = 1; });
      } else if (step === 1) {
        if (this.lookupMode === "cik") {
          if (this.form.company_cik.trim() && this.cikStatus === null) {
            await this.validateCikField();
          }
          if (this.cikStatus === "invalid") {
            this.$message.error("The CIK entered is not valid in SEC EDGAR. Please correct it before continuing.");
            return;
          }
        } else {
          if (this.form.company_ticker.trim() && this.tickerStatus === null) {
            await this.lookupTickerField();
          }
          if (this.tickerStatus === "invalid") {
            this.$message.error("The ticker entered was not found in SEC EDGAR. Please correct it before continuing.");
            return;
          }
        }
        this.$refs.companyForm.validate((valid) => { if (valid) this.currentStep = 2; });
      }
    },

    async handleSubmit() {
      this.submitting = true;
      this.submitError = null;

      const payload = {
        first_name: this.form.first_name,
        last_name:  this.form.last_name,
        email:      this.form.email,
        password:   this.form.password,
        job_title:  this.form.job_title || null,
        phone:      this.form.phone || null,
        company: {
          name:   this.form.company_name,
          ticker: this.form.company_ticker || null,
          cik:    this.form.company_cik,
        },
      };

      try {
        const response = await signup(payload);
        this.$message.success("Account created successfully!");
        this.$router.push({
          name: "SignupSuccess",
          query: { user_id: response.data.user_id, name: this.form.first_name },
        });
      } catch (err) {
        this.submitError = err.message;
        this.$message.error("Signup failed. Please check the details and try again.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────── */
.auth-page {
  display: flex;
  min-height: 100vh;
}

/* ── Left panel ─────────────────────────────────────── */
.auth-left {
  width: 42%;
  background: linear-gradient(160deg, #0a0e1f 0%, #0d1525 60%, #0f1e30 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px 56px;
  position: relative;
  overflow: hidden;
}

.auth-left::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 20% 50%, rgba(26, 86, 219, 0.12) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 20%, rgba(26, 86, 219, 0.08) 0%, transparent 50%);
}

.auth-left-content {
  position: relative;
  z-index: 1;
}

.brand-logo {
  margin-bottom: 80px;
}

.brand-logo-img {
  height: 28px;
  width: auto;
  filter: brightness(0) invert(1);
}


.auth-tagline {
  font-size: 36px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 20px;
}

.auth-description {
  font-size: 15px;
  color: #94a3b8;
  line-height: 1.7;
  max-width: 320px;
}

.powered-by {
  position: relative;
  z-index: 1;
  font-size: 13px;
  color: #64748b;
}

.powered-by strong { color: #94a3b8; }

/* ── Right panel ─────────────────────────────────────── */
.auth-right {
  flex: 1;
  background: #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 56px 60px;
  overflow-y: auto;
}

.auth-form-wrapper {
  width: 100%;
  max-width: 560px;
}

.form-logo {
  margin-bottom: 40px;
}

.form-logo-img {
  height: 26px;
  width: auto;
}


/* ── Stepper ─────────────────────────────────────────── */
.stepper {
  display: flex;
  align-items: flex-start;
  margin-bottom: 36px;
  gap: 0;
}

.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
}

.stepper-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #d0d5dd;
  background: #ffffff;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  z-index: 1;
  transition: all 0.2s;
}

.stepper-item.active .stepper-circle {
  background: #1a56db;
  border-color: #1a56db;
  color: #ffffff;
}

.stepper-item.done .stepper-circle {
  background: #1a56db;
  border-color: #1a56db;
  color: #ffffff;
}

.stepper-label {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 6px;
  text-align: center;
  max-width: 80px;
  line-height: 1.3;
}

.stepper-item.active .stepper-label {
  color: #1a56db;
  font-weight: 600;
}

.stepper-line {
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #e4e7ec;
  z-index: 0;
}

.stepper-item.done .stepper-line {
  background: #1a56db;
}

/* ── Form ─────────────────────────────────────────────── */
.form-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a56db;
  margin-bottom: 10px;
}

.form-title-bar {
  width: 40px;
  height: 4px;
  background: #1a56db;
  border-radius: 2px;
  margin-bottom: 28px;
}

.signup-form { width: 100%; }

.field-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 700;
  color: #0d1525;
  margin: 20px 0 8px;
}

.btn-submit {
  width: 100%;
  margin-top: 12px;
  background: #1a56db;
  border-color: #1a56db;
  font-size: 15px;
  font-weight: 600;
  height: 48px;
  border-radius: 4px;
}

.btn-submit:hover,
.btn-submit:focus {
  background: #1447c0;
  border-color: #1447c0;
}

.btn-back {
  height: 48px;
  border-radius: 4px;
  font-size: 15px;
}

.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.btn-row .btn-submit { flex: 1; margin-top: 0; }

.submit-alert { margin-bottom: 16px; }

/* ── Review ──────────────────────────────────────────── */
.review-section {
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
}

.review-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #344054;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.review-field {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.review-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.review-value {
  font-size: 14px;
  color: #0d1525;
  font-weight: 500;
}

/* ── Footer link ─────────────────────────────────────── */
.lookup-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.lookup-toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  white-space: nowrap;
}

.lookup-toggle :deep(.el-radio-button__inner) {
  font-size: 13px;
  padding: 7px 16px;
}

.lookup-toggle :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  background-color: #1a56db;
  border-color: #1a56db;
  box-shadow: -1px 0 0 0 #1a56db;
}

.cik-feedback {
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cik-feedback.valid   { color: #16a34a; }
.cik-feedback.invalid { color: #dc2626; }

.email-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1d4ed8;
  margin: 16px 0 4px;
}

.alt-link {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #64748b;
}

.alt-link a {
  color: #1a56db;
  font-weight: 600;
  text-decoration: none;
}

.alt-link a:hover { text-decoration: underline; }

/* ── Password rules ──────────────────────────────────── */
.pwd-rules {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
}

.pwd-rule {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 3px;
}

.rule-ok   { color: #16a34a; }
.rule-fail { color: #94a3b8; }

/* ── Element UI overrides ───────────────────────────── */
.signup-form :deep(.el-input__inner) {
  height: 44px;
  border-radius: 4px;
  border-color: #d0d5dd;
  font-size: 14px;
}

.signup-form :deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f8fafc;
  color: #344054;
  border-color: #e4e7ec;
  cursor: default;
}

.signup-form :deep(.el-input__inner:focus) {
  border-color: #1a56db;
}

.signup-form :deep(.el-form-item__label) {
  font-weight: 600;
  color: #344054;
  font-size: 14px;
}

@media (max-width: 768px) {
  .auth-left { display: none; }
  .auth-right { padding: 32px 24px; }
}
</style>
